import { Theme } from '@eversports/react-components/design-tokens/theme'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import { assertNever } from '@eversports/utility-types'
import { makeStyles } from '@mui/styles'
import React from 'react'

import { Localized } from '../../../localization/react'
import BookableItem from '../../../typings/BookableItem'
import { SettleWith } from '../../../typings/Product'

import Address from './Address'
import BookingDetailsCancellationConditions from './BookingDetailsCancellationConditions'
import BookingDetailsName from './BookingDetailsName'
import BookingDetailsSettleWith from './BookingDetailsSettleWith'
import BookingDetailsTimeBlock from './BookingDetailsTimeBlock'

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontSize: theme.fontSizing.large,
    [theme.breakpoints.down('md')]: {
      fontSize: theme.fontSizing.default,
    },
  },
}))

interface Props {
  bookableItem: BookableItem
  settleWith?: SettleWith | null
}

const BookableItemBookingDetailsContentInformation: React.FC<React.PropsWithChildren<Props>> = ({
  bookableItem,
  settleWith,
}) => {
  switch (bookableItem.__typename) {
    case 'CourtBookableItem':
      return (
        <div>
          <Flex flexDirection="column" marginBottom="wide">
            <BookingDetailsName
              type="court"
              venueName={bookableItem.venue.name}
              sport={bookableItem.sport.name}
              courtName={bookableItem.court.name}
            />
            <BookingDetailsCancellationConditions cancellationConditions={bookableItem.cancellationCondition} />
          </Flex>
          <Flex flexDirection="column" verticalSpacing="default">
            <Address location={bookableItem.venue.location!} />
            <BookingDetailsTimeBlock type="court" timeRange={bookableItem.timeRange} />
          </Flex>
        </div>
      )
    case 'EventBookableItem':
      return (
        <div>
          <Flex flexDirection="column" marginBottom="wide">
            <BookingDetailsName
              type="event"
              venueName={bookableItem.venue.name}
              sport={bookableItem.sport.name}
              activityName={bookableItem.activityName}
            />
            <BookingDetailsCancellationConditions cancellationConditions={bookableItem.cancellationCondition} />
          </Flex>
          <Flex flexDirection="column" verticalSpacing="default">
            <Address location={bookableItem.location} />
            {settleWith && settleWith.__typename === 'Ticket' ? (
              <BookingDetailsTimeBlock type="event" timeRanges={settleWith.validForSessions} />
            ) : (
              <BookingDetailsTimeBlock type="event" timeRanges={bookableItem.eventSessions} />
            )}
            {settleWith && <BookingDetailsSettleWith settleWith={settleWith} />}
          </Flex>
        </div>
      )
    default:
      return assertNever(bookableItem)
  }
}

const BookableItemBookingDetailsContent: React.FC<React.PropsWithChildren<Props>> = ({ bookableItem, settleWith }) => {
  const classes = useStyles()

  return (
    <Flex flexDirection="column" verticalSpacing={['dense', 'wide']}>
      <Heading is="h1" color="text.disabled" className={classes.heading} marginTop={['default', 'default', 'none']}>
        <Localized id="booking-details" />
      </Heading>
      <BookableItemBookingDetailsContentInformation bookableItem={bookableItem} settleWith={settleWith} />
    </Flex>
  )
}

export default BookableItemBookingDetailsContent
